import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import BqnContext from "common/BqnContext";
import { retrieveRange } from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import {
  PERIOD_TO_HOURS,
  altPeriodScopeChoices1MonthOrLess,
  altPeriodScopeChoices,
  HOURS_TO_PERIOD,
} from "common/constants";
import { CpuChart } from "./CpuChart";
import ColumnsGrid from "common/layouts/ColumnsGrid";
import useHoursOfReturnView from "common/hooks/useHoursOfReturnView";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const Cell = styled.div`
  display: flex;
  flex-flow: column nowrap;
`
const SelectsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  margin-bottom: 15px;
  @media screen and (max-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const RangeContainer = styled.div`
  margin: 20px 0;
`;

const _scaleScopeChoices = [
  { value: "Adaptive", label: "Adaptive" },
  { value: "Full", label: "Full" },
];

const _chartScopeChoices = [
  { value: "busy", label: "Busy" },
  { value: "idle", label: "Idle" },
  { value: "system", label: "System" },
  { value: "user", label: "User" },
  { value: "soft", label: "Soft-IRQ" },
];

const _defaultCpuOptions = [
  { value: "All", label: "All" },
  { value: "Average", label: "Average" },
];

const SysStatsCpuReport = ({ returnView = null }) => {
  const { bqnId, clusterStatus, onlineBqns } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const hoursOfReturnView = useHoursOfReturnView(returnView);
  const [period, setPeriod] = useState(
    HOURS_TO_PERIOD[hoursOfReturnView] ?? "Day",
  );
  const [scale, setScale] = useState("Full");
  const [chart, setChart] = useState("busy");
  const [cpu, setCpu] = useState("All");
  const [cpuChoices, setCpuChoices] = useState(_defaultCpuOptions);

  const [initialRange, setInitialRange] = useState();
  const [range, setRange] = useState();
  const shouldShowGrid =
    mode === "cluster" && bqnId === "all" && onlineBqns.length > 1;

  // Nothing is asked while the BQN context has not loaded the BQN selected:
  // the commands would not carry its option, and their responses would be
  // those of the whole cluster. Without a range there is no chart, and the
  // effect runs again when the BQN selected is known.
  useEffect(() => {
    if (bqnId === undefined) {
      return;
    }

    setRange(undefined);
    const hours = PERIOD_TO_HOURS[period];
    retrieveRange(hours, mode, bqnId).then(({ from, to }) =>
      setInitialRange({ from, to })
    );
  }, [period, mode, bqnId]);

  useEffect(() => {
    setCpuChoices(_defaultCpuOptions);
  }, [period, bqnId, chart]);

  return (
    <Wrapper>
      <SelectsWrapper>
        <SelectWrapper>
          <SelectInputWithIcon
            title="Scale"
            name="Scale"
            icon="zoom_out_map"
            selected={scale}
            onChange={({ target }) => setScale(target.value)}
            options={_scaleScopeChoices}
          />
        </SelectWrapper>
        <SelectWrapper>
          <SelectInputWithIcon
            title="Chart"
            name="Chart"
            icon="timeline"
            selected={chart}
            onChange={({ target }) => setChart(target.value)}
            options={_chartScopeChoices}
          />
        </SelectWrapper>
        <SelectWrapper>
          <SelectInputWithIcon
            title="Cpu"
            name="Cpu"
            icon="memory"
            selected={cpu}
            onChange={({ target }) => setCpu(target.value)}
            options={cpuChoices}
          />
        </SelectWrapper>
        <SelectWrapper>
          <SelectInputWithIcon
            title="Date Range"
            name="Date Range"
            icon="date_range"
            selected={period}
            onChange={({ target }) => setPeriod(target.value)}
            options={
              cpuChoices.length > 100
                ? altPeriodScopeChoices1MonthOrLess
                : altPeriodScopeChoices
            }
          />
        </SelectWrapper>
      </SelectsWrapper>
      {initialRange && !shouldShowGrid && (
        <ColumnsGrid columns={1} minWidth="7cm" gap="0px">
          {mode === "cluster" && bqnId !== "all" && (
            <h4 className="margin-b-10"> BQN {bqnId} </h4>
          )}
          <CpuChart
            bqnId={bqnId}
            period={period}
            setCpuChoices={setCpuChoices}
            range={range}
            initialRange={initialRange}
            chart={chart}
            cpu={cpu}
            mode={mode}
            scale={scale}
            big={true}
          />
        </ColumnsGrid>
      )}
      {initialRange && shouldShowGrid && (
        <ColumnsGrid columns={2} minWidth="7cm" gap="0px">
          {onlineBqns.map((bqn) => (
            <Cell key={bqn.bqnId}>
              <h4 className="margin-b-10"> BQN {bqn.bqnId} </h4>
              <CpuChart
                bqnId={bqn.bqnId}
                period={period}
                setCpuChoices={setCpuChoices}
                range={range}
                initialRange={initialRange}
                chart={chart}
                cpu={cpu}
                mode={mode}
                scale={scale}
                big={false}
              />
            </Cell>
          ))}
        </ColumnsGrid>
      )}
      <RangeContainer>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </RangeContainer>
    </Wrapper>
  );
};

export default SysStatsCpuReport;
